/**
 * Public landing hero — an IMAGE-FIRST banner carousel driven by admin-managed
 * banners (/public/banners). Each banner is a full-bleed designed image; the
 * text/buttons are an optional overlay (per-banner `showOverlay`). A banner with
 * no image falls back to the brand gradient + centred text. If no banners exist
 * (or while loading) a built-in default banner keeps the hero from being empty.
 *
 * `BannerSlideView` is exported so the admin editor can render a pixel-faithful
 * live preview using the exact same slide markup as the live site.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { cn } from '../ui';
import { RhizosphereArt } from './illustrations';
import { Hero } from './components/sections/Hero';
import { useGsapDraw } from '../ui/motion';

/** Design line-arrow (lucide-react v1.21 has no matching arrow weight). */
function Arrow({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export interface Banner {
  id: string;
  eyebrow?: string | null;
  title: string;
  subtitle?: string | null;
  imageUrl?: string | null;
  showOverlay?: boolean;
  /** CSS object-position for the cover image (e.g. "50% 40%") — keeps the subject in frame
   *  across a tall phone hero and a wide desktop hero from ONE image. Null → centred. */
  focalPoint?: string | null;
  ctaLabel?: string | null;
  ctaLink?: string | null;
  secondaryCtaLabel?: string | null;
  secondaryCtaLink?: string | null;
}

export const DEFAULT_BANNER: Banner = {
  id: 'default',
  eyebrow: 'Sustainable Bio-Innovation for Indian Farmers',
  title: 'Nurture. Grow. Protect.',
  subtitle:
    'Achieve food security, end hunger, improve nutrition and promote sustainable agriculture through bio-innovation — for healthier soil, stronger plants and better yields.',
  imageUrl: null,
  showOverlay: true,
  ctaLabel: 'Explore Products', ctaLink: '/catalog',
  secondaryCtaLabel: 'Our Story', secondaryCtaLink: '/about',
};

const AUTO_MS = 6500;

function Cta({ label, link, variant }: { label: string; link: string; variant: 'primary' | 'ghost' }) {
  const cls =
    variant === 'primary'
      ? 'inline-flex items-center gap-2 rounded-[10px] bg-white! px-5 py-3 text-[15px] font-bold text-[#0f3d1f]! shadow-lift transition-transform hover:-translate-y-0.5 sm:px-6 sm:py-3.5 sm:text-base'
      : 'inline-flex items-center gap-2 rounded-[10px] border border-white/40 bg-white/15! px-5 py-3 text-[15px] font-bold text-white! backdrop-blur transition-colors hover:bg-white/25! sm:px-6 sm:py-3.5 sm:text-base';
  const inner = (
    <>
      {label}
      {variant === 'primary' && <Arrow className="size-[17px]" />}
    </>
  );
  return link.startsWith('/') ? (
    <Link to={link} className={cls}>{inner}</Link>
  ) : (
    <a href={link} className={cls} target="_blank" rel="noopener noreferrer">{inner}</a>
  );
}

/**
 * The text/CTA block — left-weighted hero copy laid over the image scrim or the
 * brand gradient (`centered` keeps the no-image branch from clinging to one edge
 * on very wide screens; the design itself is left-aligned within the container).
 */
function BannerContent({ b, centered }: { b: Banner; centered: boolean }) {
  return (
    <div className="relative z-[2] mx-auto flex h-full w-full max-w-[1200px] items-center px-5 py-10 sm:px-8 sm:py-16 lg:py-[72px]">
      <div className={cn('max-w-[660px] text-white', centered && 'mx-auto text-center')}>
        {b.eyebrow && (
          <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] backdrop-blur sm:gap-2.5 sm:px-4 sm:py-2 sm:text-xs sm:tracking-[0.08em]">
            <span className="size-1.5 shrink-0 rounded-full bg-harvest-400 ring-4 ring-harvest-400/30 sm:size-2" />
            {b.eyebrow}
          </span>
        )}
        <h1 className="mt-3.5 whitespace-pre-line text-[2rem] font-black leading-[1.04] tracking-tight [text-shadow:0_4px_36px_rgba(0,0,0,0.32)] sm:mt-[18px] sm:text-5xl sm:leading-[1.0] lg:text-[clamp(56px,7vw,84px)] lg:leading-[.97]">
          {b.title}
        </h1>
        {b.subtitle && (
          <p className={cn('mt-4 line-clamp-4 max-w-[52ch] text-sm leading-relaxed text-white/90 [text-shadow:0_2px_12px_rgba(0,0,0,0.3)] sm:mt-[22px] sm:line-clamp-none sm:text-base lg:text-lg', centered && 'mx-auto')}>
            {b.subtitle}
          </p>
        )}
        {(b.ctaLabel || b.secondaryCtaLabel) && (
          <div className={cn('mt-6 flex flex-wrap gap-3 sm:mt-8 sm:gap-3.5', centered && 'justify-center')}>
            {b.ctaLabel && b.ctaLink && <Cta label={b.ctaLabel} link={b.ctaLink} variant="primary" />}
            {b.secondaryCtaLabel && b.secondaryCtaLink && <Cta label={b.secondaryCtaLabel} link={b.secondaryCtaLink} variant="ghost" />}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One banner rendered to fill its (positioned) parent — image-first with an
 * optional text overlay, or a gradient + centred text when there's no image.
 * Shared by the live carousel and the admin editor preview.
 */
export function BannerSlideView({ b }: { b: Banner }) {
  const hasImage = !!b.imageUrl;
  const showText = !hasImage || b.showOverlay !== false;
  // Line-art draws itself in on the gradient (no-image) hero — only mounted there.
  const artRef = useGsapDraw<HTMLDivElement>({
    duration: 1.5,
    stagger: 0.02,
    selector: 'path, line, polyline, polygon, circle, ellipse',
  });
  return (
    <div className="relative flex size-full items-center overflow-hidden">
      {hasImage ? (
        <>
          <img src={b.imageUrl ?? undefined} alt={b.title} className="absolute inset-0 size-full object-cover" style={{ objectPosition: b.focalPoint || '50% 50%' }} />
          {/*
            THE SAME SCRIM THE DEFAULT HERO USES — five layers, not two.

            An admin banner is an image swap, not a downgrade. This slide used to carry only a weak
            92deg wash and a faint bottom radial, so the moment anyone activated a custom banner the
            hero visibly lost its depth: the headline sat on raw photograph, the navigation floated
            over whatever happened to be bright at the top of the image, and the section ended on a
            hard horizontal cut instead of melting into the page below. None of that was "lost" —
            it lives in sections/Hero.tsx, and this component never had it.

            Layer by layer, matching Hero:
              1. a flat 25% darkening, so text contrast never depends on the photo,
              2/3. the directional wash — down the screen when the viewport is portrait, across it
                   when landscape, because the copy sits left on a wide screen and top on a narrow one,
              4. a top band, so the transparent nav always has something to sit on,
              5. a bottom fade to the page canvas, which is what stops the hero looking like a
                 photograph pasted onto the page.
          */}
          {showText && (
            <>
              <div data-hero="scrim" className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-night/25" />
                <div className="absolute inset-0 bg-gradient-to-b from-night/85 via-night/15 to-transparent landscape:hidden" />
                <div className="absolute inset-0 hidden bg-gradient-to-r from-night/92 via-night/55 to-transparent landscape:block" />
                <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-night/55 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-canvas md:h-32" />
              </div>
              <BannerContent b={b} centered={false} />
            </>
          )}
        </>
      ) : (
        <>
          {/* Richer radial gradient (light leaf → deep bay) for real depth. */}
          <div className="absolute inset-0 bg-[radial-gradient(130%_130%_at_78%_-12%,#5bbb3e_0%,#1b6a33_46%,#0f3d1f_100%)]" />
          {/* Fine dot-grid texture — subtle "engineered/scientific" surface. */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(#fff_1px,transparent_1px)] [background-size:22px_22px]" />
          {/* Soft light blooms. */}
          <div className="pointer-events-none absolute -right-24 -top-24 size-[26rem] rounded-full bg-brand-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-40 -left-24 size-[26rem] rounded-full bg-harvest-400/10 blur-3xl" />
          {/* Right-side rhizosphere illustration draws in + floats. Hidden below lg where
              the copy needs the full width; decorative (aria-hidden via the SVG). */}
          <div className="pointer-events-none absolute right-[5%] top-1/2 hidden -translate-y-1/2 lg:block xl:right-[9%]">
            <div className="animate-float">
              <div ref={artRef}>
                <RhizosphereArt className="h-[58vh] max-h-[500px] w-auto opacity-90 [filter:drop-shadow(0_8px_30px_rgba(0,0,0,0.25))]" />
              </div>
            </div>
          </div>
          <BannerContent b={b} centered={false} />
        </>
      )}
    </div>
  );
}

export function BannerCarousel({ fallback }: { fallback?: ReactNode } = {}) {
  const { data } = useQuery({
    queryKey: ['public-banners'],
    queryFn: () => request<Banner[]>({ url: '/public/banners' }),
  });

  const active = data ?? [];
  /**
   * ONE banner, never a carousel.
   *
   * The hero used to cycle every active banner with arrows, dots, a "1 / 3" counter and an autoplay
   * timer. A rotating hero is a poor deal on a landing page: the first slide is the only one most
   * visitors ever see, the movement pulls the eye away from the headline it is meant to support,
   * and the controls crowd the one part of the site that should be calm. So the site shows the
   * FIRST active banner and nothing else.
   *
   * The slicing happens here rather than by deleting the slide machinery below, because everything
   * downstream is already correctly conditioned on `count > 1` — with one banner the arrows, dots,
   * counter, pause button and the autoplay interval all switch themselves off, and the admin
   * preview keeps rendering the same slide markup it always did. Activating a second banner in
   * /admin/banners is still meaningful: it becomes the one shown when the first is deactivated.
   */
  const banners = active.length > 0 ? active.slice(0, 1) : [DEFAULT_BANNER];
  const count = banners.length;
  const [index, setIndex] = useState(0);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [focusPaused, setFocusPaused] = useState(false);
  const [manualPaused, setManualPaused] = useState(false);
  const [reduced, setReduced] = useState(false);

  // Honour prefers-reduced-motion — never auto-advance for users who ask for less motion.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  useEffect(() => { if (index >= count) setIndex(0); }, [count, index]);

  // Autoplay pauses on hover, on keyboard focus within the carousel, on the manual
  // pause toggle, and entirely under reduced motion (WCAG 2.2.2 Pause, Stop, Hide).
  const autoPaused = hoverPaused || focusPaused || manualPaused || reduced;
  useEffect(() => {
    if (count <= 1 || autoPaused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), AUTO_MS);
    return () => clearInterval(t);
  }, [count, autoPaused]);

  const go = (i: number) => setIndex((i + count) % count);
  const activeIndex = Math.min(index, count - 1);
  const playing = !manualPaused && !reduced;

  // When a `fallback` (the premium default hero) is provided, show it while banners load AND when the admin
  // has no active custom banner — so a custom banner is a true OPT-IN override, not a replacement. Placed
  // AFTER all hooks (Rules of Hooks) — the hooks above run harmlessly on the single DEFAULT_BANNER meanwhile.
  // Without a fallback (legacy LandingPage usage) we keep the built-in DEFAULT_BANNER so the hero is never empty.
  if (fallback !== undefined && active.length === 0) return <>{fallback}</>;

  return (
    <section
      // An image banner renders the Hero, which sets its own 100svh height; the min-heights here are
      // for the gradient banner, which has no intrinsic height of its own.
      className={cn(
        'relative flex w-full items-center overflow-hidden bg-brand-900',
        banners[0]?.imageUrl ? 'min-h-[100svh]' : 'min-h-[460px] sm:min-h-[540px] lg:min-h-[clamp(600px,72vh,740px)]',
      )}
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
      onFocusCapture={() => setFocusPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusPaused(false);
      }}
      // One static banner: announcing it as a carousel would promise a control set that is not there.
      aria-label="Highlights"
    >
      {banners.map((b, i) => (
        <div
          key={b.id}
          className={cn('absolute inset-0 transition-opacity duration-700', i === activeIndex ? 'opacity-100' : 'pointer-events-none opacity-0')}
          aria-hidden={i !== index}
        >
          {/*
            An image banner renders through the REAL Hero, not through BannerSlideView.

            This is the whole fix for "why does production look better than my custom banner?" —
            production was showing sections/Hero.tsx and the custom banner was showing a plainer
            component that never had the scrim, the display type, the stats row, the scroll cue or
            the solid green button. Handing the Hero the admin's image and copy makes a banner what
            everyone assumed it already was: the same designed hero with a different picture.

            BannerSlideView survives for the no-image (gradient + rhizosphere art) banner, which is a
            genuinely different composition, and for the admin editor's live preview.
          */}
          {b.imageUrl ? <Hero override={b} /> : <BannerSlideView b={b} />}
        </div>
      ))}

      {count > 1 && (
        <>
          {/* Circular glass arrows — hidden on mobile (they'd overlap the text); dots remain. */}
          <button type="button" aria-label="Previous banner" onClick={() => go(index - 1)}
            className="absolute left-[clamp(10px,2vw,22px)] top-1/2 z-[3] hidden size-[46px] -translate-y-1/2 place-items-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/30 sm:grid">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <button type="button" aria-label="Next banner" onClick={() => go(index + 1)}
            className="absolute right-[clamp(10px,2vw,22px)] top-1/2 z-[3] hidden size-[46px] -translate-y-1/2 place-items-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/30 sm:grid">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>

          {/* Bottom-center dots: active = wide harvest pill, others = small white dots. */}
          <div className="absolute bottom-[22px] left-1/2 z-[3] flex -translate-x-1/2 items-center gap-2.5">
            {banners.map((b, i) => (
              <button key={b.id} type="button" aria-label={`Go to banner ${i + 1}`} aria-current={i === index} onClick={() => go(i)}
                className={i === activeIndex ? 'h-2 w-7 rounded-full bg-harvest-300 transition-all' : 'size-2 rounded-full bg-white/50 transition-all hover:bg-white/80'} />
            ))}
          </div>

          {/* Bottom-right slide counter. */}
          <div className="absolute bottom-5 right-[clamp(16px,4vw,32px)] z-[3] text-[13px] font-bold tracking-[0.05em] text-white/85">
            {activeIndex + 1} / {count}
          </div>

          {/* Bottom-left pause/play — the accessible way to stop the autoplay
              (keyboard + touch reachable). Hidden under reduced motion, where the
              carousel never auto-advances in the first place. */}
          {!reduced && (
            <button
              type="button"
              onClick={() => setManualPaused((p) => !p)}
              aria-label={playing ? 'Pause banner autoplay' : 'Resume banner autoplay'}
              aria-pressed={!playing}
              className="absolute bottom-[18px] left-[clamp(16px,4vw,32px)] z-[3] grid size-10 place-items-center rounded-full border border-white/30 bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              {playing ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
          )}
        </>
      )}
    </section>
  );
}
